import { Download, Share2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import {
  clearInstallHandoff,
  hasInstallPrompt,
  isIosBrowser,
  isPwaInstallHandoff,
  isStandalonePwa,
  preparePwaInstall,
  promptPwaInstall,
  subscribeInstallPrompt,
} from './pwaInstall';

const COPY = {
  'pt-BR': {
    title: 'Instale o Sorteia',
    body: 'Instale o app oficial para abrir o Sorteia direto da tela inicial.',
    install: 'Instalar aplicativo',
    continue: 'Continuar no navegador',
    manual: 'Se o botão de instalação não aparecer, abra o menu do navegador e escolha “Instalar app”.',
    ios: 'Toque em Compartilhar e depois em “Adicionar à Tela de Início”.',
  },
  es: {
    title: 'Instalá Sorteia',
    body: 'Instalá la app oficial para abrir el Player directamente desde tu pantalla de inicio.',
    install: 'Instalar aplicación',
    continue: 'Continuar en el navegador',
    manual: 'Si no aparece la instalación, abrí el menú del navegador y elegí “Instalar aplicación”.',
    ios: 'Tocá Compartir y luego “Agregar a inicio”.',
  },
  en: {
    title: 'Install Sorteia',
    body: 'Install the official app to open the Player directly from your home screen.',
    install: 'Install app',
    continue: 'Continue in browser',
    manual: 'If the install prompt does not appear, open the browser menu and choose “Install app”.',
    ios: 'Tap Share, then “Add to Home Screen”.',
  },
} as const;

export function PwaInstallGate() {
  const language = useI18nStore((state) => state.language);
  const [visible, setVisible] = useState(() => isPwaInstallHandoff() && !isStandalonePwa());
  const [promptReady, setPromptReady] = useState(hasInstallPrompt);
  const [showManual, setShowManual] = useState(false);
  const copy = COPY[language];
  const ios = isIosBrowser();

  useEffect(() => {
    if (!visible) return;
    const refresh = () => setPromptReady(hasInstallPrompt());
    const unsubscribe = subscribeInstallPrompt(refresh);
    void preparePwaInstall().then(refresh);
    const fallback = window.setTimeout(() => setShowManual(true), 2500);
    return () => {
      unsubscribe();
      window.clearTimeout(fallback);
    };
  }, [visible]);

  if (!visible) return null;

  const continueInBrowser = () => {
    clearInstallHandoff();
    setVisible(false);
  };

  return (
    <div className="ds-pwa-install" role="dialog" aria-modal="true" aria-label={copy.title} data-pwa-install-gate="true">
      <div className="ds-pwa-install-card">
        <img src="/brand/android-chrome-192x192.png" alt="" />
        <h2>{copy.title}</h2>
        <p>{copy.body}</p>
        {ios ? <p className="ds-pwa-install-hint"><Share2 size={17} aria-hidden="true" />{copy.ios}</p> : null}
        {!ios && showManual && !promptReady ? <p className="ds-pwa-install-hint">{copy.manual}</p> : null}
        {!ios ? (
          <button
            className="ds-btn ds-btn-p tap"
            type="button"
            onClick={() => {
              void promptPwaInstall().then((choice) => {
                if (choice === 'accepted') continueInBrowser();
                else if (choice === 'unavailable') setShowManual(true);
              });
            }}
          >
            <Download size={18} aria-hidden="true" />{copy.install}
          </button>
        ) : null}
        <button className="ds-pwa-install-skip tap" type="button" onClick={continueInBrowser}>{copy.continue}</button>
      </div>
    </div>
  );
}
